import axios from 'axios';
import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Clock, User, FileText, DollarSign, CheckCircle, FolderOpen,
  Send, Eye, Palette, CalendarCheck, Truck, Wrench, Flag,
  Mail, MessageSquare, Bell,
} from 'lucide-react';

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

// 🆕 [Phase 93 — 2026-07-08] Catalogue étendu — inclut envois Phase 92 + paiements individuels
const EVT_CONFIG = {
  dossier_client: { icon: FolderOpen, color: 'text-emerald-600', bg: 'bg-emerald-50' },
  creation_projet: { icon: FileText, color: 'text-blue-600', bg: 'bg-blue-50' },
  soumission_creee: { icon: FileText, color: 'text-purple-600', bg: 'bg-purple-50' },
  soumission_envoyee: { icon: Send, color: 'text-sky-600', bg: 'bg-sky-50' },
  soumission_ouverte: { icon: Eye, color: 'text-indigo-600', bg: 'bg-indigo-50' },
  signature: { icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50' },
  code_couleur_recu: { icon: Palette, color: 'text-pink-600', bg: 'bg-pink-50' },
  client_cedule_production: { icon: CalendarCheck, color: 'text-amber-600', bg: 'bg-amber-50' },
  depot_recu: { icon: DollarSign, color: 'text-lime-600', bg: 'bg-lime-50' },
  paiement_final_recu: { icon: DollarSign, color: 'text-emerald-700', bg: 'bg-emerald-50' },
  paiement_partiel: { icon: DollarSign, color: 'text-emerald-600', bg: 'bg-emerald-50' },
  utilisation_equipements_terminee: { icon: Truck, color: 'text-orange-600', bg: 'bg-orange-50' },
  contrat_termine: { icon: Flag, color: 'text-green-700', bg: 'bg-green-50' },
  facture: { icon: DollarSign, color: 'text-orange-600', bg: 'bg-orange-50' },
  facture_creee: { icon: DollarSign, color: 'text-orange-600', bg: 'bg-orange-50' },
  facture_modifiee: { icon: DollarSign, color: 'text-orange-500', bg: 'bg-orange-50' },
  bon_travail_cree: { icon: Wrench, color: 'text-slate-600', bg: 'bg-slate-50' },
  service_call_cree: { icon: Wrench, color: 'text-orange-500', bg: 'bg-orange-50' },
  rdv_soumission_cedulee: { icon: CalendarCheck, color: 'text-blue-600', bg: 'bg-blue-50' },
  heures_budgetees_modifiees: { icon: Clock, color: 'text-violet-600', bg: 'bg-violet-50' },
  a_l_heure_modifie: { icon: Clock, color: 'text-teal-600', bg: 'bg-teal-50' },
  // 🆕 [Phase 93] Nouveaux types Phase 92
  email_facture_envoye: { icon: Mail, color: 'text-blue-700', bg: 'bg-blue-50' },
  sms_rappel_manuel: { icon: Bell, color: 'text-orange-700', bg: 'bg-orange-50' },
  sms_paiement_confirmation: { icon: MessageSquare, color: 'text-emerald-700', bg: 'bg-emerald-50' },
  sms_generique: { icon: MessageSquare, color: 'text-slate-500', bg: 'bg-slate-50' },
};

// 🆕 [Phase 93] Format date narratif FR-CA : "15 janvier 2026"
const MOIS_FR = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin',
                 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];

function formatJourFrCA(isoDate) {
  const d = new Date(isoDate);
  if (isNaN(d.getTime())) return '—';
  const now = new Date();
  const aujourdhui = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const hier = new Date(aujourdhui);
  hier.setDate(hier.getDate() - 1);
  const dJour = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  if (dJour.getTime() === aujourdhui.getTime()) return `Aujourd'hui — ${d.getDate()} ${MOIS_FR[d.getMonth()]} ${d.getFullYear()}`;
  if (dJour.getTime() === hier.getTime()) return `Hier — ${d.getDate()} ${MOIS_FR[d.getMonth()]} ${d.getFullYear()}`;
  return `${d.getDate()} ${MOIS_FR[d.getMonth()]} ${d.getFullYear()}`;
}

function dayKey(isoDate) {
  const d = new Date(isoDate);
  if (isNaN(d.getTime())) return 'inconnu';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function OngletHistorique({ dossier }) {
  const [events, setEvents] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!dossier?.id) return;
    setLoading(true);
    const fetchTimeline = async () => {
      try {
        // 🆕 [Phase 93] Single fetch — endpoint agrégé backend
        const res = await axios.get(`${API_URL}/api/crm/dossiers/${dossier.id}/timeline`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('auth_token')}` },
        });
        setEvents(res.data?.events || []);
      } catch (e) {
        console.error('Timeline fetch failed:', e);
        setEvents([]);
      } finally {
        setLoading(false);
      }
    };
    fetchTimeline();
  }, [dossier?.id]);

  // 🆕 [Phase 93] Groupement par jour (chronologique DESC — plus récent en premier)
  const groupsByDay = (events || []).reduce((acc, evt) => {
    const key = dayKey(evt.date);
    if (!acc[key]) acc[key] = { key, label: formatJourFrCA(evt.date), events: [] };
    acc[key].events.push(evt);
    return acc;
  }, {});
  const orderedGroups = Object.values(groupsByDay).sort((a, b) => b.key.localeCompare(a.key));
  const total = (events || []).length;

  return (
    <Card data-testid="historique-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Clock className="w-5 h-5 text-gray-600" />
          Historique du Dossier
          {total > 0 && (
            <Badge variant="outline" className="ml-2 text-xs" data-testid="historique-total">
              {total} événement{total > 1 ? 's' : ''}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="text-center py-8 text-gray-500 text-sm">Chargement de la timeline…</div>
        ) : total === 0 ? (
          <div className="text-center py-12" data-testid="historique-empty">
            <Clock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Aucun événement dans l'historique</p>
          </div>
        ) : (
          <div className="space-y-6">
            {orderedGroups.map(group => (
              <section key={group.key} data-testid={`day-group-${group.key}`}>
                <h3
                  className="text-sm font-bold text-gray-700 mb-3 pb-1 border-b border-gray-200 sticky top-0 bg-white z-20"
                  data-testid={`day-header-${group.key}`}
                >
                  {group.label}
                </h3>
                <div className="relative">
                  {/* Timeline line */}
                  <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-gray-200" />
                  <div className="space-y-1">
                    {group.events.map((evt) => {
                      const config = EVT_CONFIG[evt.type] || EVT_CONFIG.creation_projet;
                      const IconComponent = config.icon;
                      const meta = evt.meta || {};
                      return (
                        <div
                          key={evt.id}
                          className="flex gap-4 relative"
                          data-testid={`historique-evt-${evt.type}`}
                        >
                          <div className={`z-10 shrink-0 w-10 h-10 rounded-full ${config.bg} ${config.color} flex items-center justify-center`}>
                            <IconComponent className="w-4 h-4" />
                          </div>
                          <div className="flex-1 pb-4 pt-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-medium text-sm text-gray-900">{evt.titre}</p>
                              <span className="text-[10px] text-gray-400">
                                {new Date(evt.date).toLocaleTimeString('fr-CA', { hour: '2-digit', minute: '2-digit' })}
                              </span>
                              {typeof meta.montant === 'number' && meta.montant > 0 && (
                                <Badge className="text-[10px] bg-emerald-50 text-emerald-700 border border-emerald-200">
                                  {meta.montant.toLocaleString('fr-CA', { style: 'currency', currency: 'CAD', maximumFractionDigits: 2 })}
                                </Badge>
                              )}
                            </div>
                            {evt.description && (
                              <p className="text-xs text-gray-500 mt-0.5">{evt.description}</p>
                            )}
                            {meta.notes && (
                              <p className="text-[11px] text-gray-500 italic mt-0.5">« {meta.notes} »</p>
                            )}
                            {evt.auteur && (
                              <p className="text-[10px] text-gray-400 mt-0.5 flex items-center gap-1">
                                <User className="w-3 h-3" /> {evt.auteur}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </section>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
